import { LucideIcon } from '../../icons/LucideIcon';
import { resolveSectionLucideIcon } from '../../icons/ctaLucideMap';
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

export function HeroUtilityReservation({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const ctaSecondary = pickBlockField(block.content, 'ctaSecondary', locale);
  const ctaSecondaryIntent = readBlockCtaIntent(block.content, locale, {
    intentFieldKey: 'ctaSecondaryIntent',
    hrefFieldKey: 'ctaSecondaryHref',
    fallback: 'browse',
  });
  const ctaSecondaryHref = resolveBlockCtaHref(ctaSecondaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaSecondaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const address = pickBlockField(block.content, 'address', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const addressIconKey = pickBlockField(block.content, 'addressIcon', locale);
  const hoursIconKey = pickBlockField(block.content, 'hoursIcon', locale);
  const heroImage = pickMediaUrl(block, 'heroImage');
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 640, 72) : undefined;
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [320, 480, 640], 72)
    : undefined;
  const target = isActive ? activeEditTarget : null;
  const addressIcon = resolveSectionLucideIcon(addressIconKey, 'location');
  const hoursIcon = resolveSectionLucideIcon(hoursIconKey, 'hours');

  return (
    <section
      className={['ws-hero-desk', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="top"
    >
      {heroImageSrc ? (
        <EditableMedia
          fieldKey="heroImage"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-desk__backdrop"
          src={heroImageSrc}
          srcSet={heroImageSrcSet}
          sizes="100vw"
          alt=""
          width={640}
          height={360}
          loading="eager"
          decoding="async"
          aria-hidden
        />
      ) : null}
      <div id="about" className="ws-hero-desk__panel">
        <EditableContent
          as={Heading}
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-desk__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="subheading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-desk__lead"
        >
          {subheading}
        </EditableContent>
        {ctaPrimary || ctaSecondary ? (
          <div className="ws-hero-desk__actions">
            {ctaPrimary ? (
              <EditableContent
                as="a"
                fieldKey="ctaPrimary"
                editable={editable}
                activeEditTarget={target}
                className="ws-hero-desk__cta"
                href={ctaPrimaryHref}
              >
                {ctaPrimary}
              </EditableContent>
            ) : null}
            {ctaSecondary ? (
              <EditableContent
                as="a"
                fieldKey="ctaSecondary"
                editable={editable}
                activeEditTarget={target}
                className="ws-hero-desk__cta-secondary"
                href={ctaSecondaryHref}
              >
                {ctaSecondary}
              </EditableContent>
            ) : null}
          </div>
        ) : null}
        {(address || hours || editable) && (
          <ul className="ws-hero-desk__facts">
            {address || editable ? (
              <li className="ws-hero-desk__fact">
                <EditableContent
                  as="span"
                  fieldKey="addressIcon"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-hero-desk__fact-icon-hit"
                >
                  <LucideIcon icon={addressIcon} size="sm" className="ws-hero-desk__fact-icon" />
                </EditableContent>
                <EditableContent
                  fieldKey="address"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-hero-desk__fact-text"
                >
                  {address}
                </EditableContent>
              </li>
            ) : null}
            {hours || editable ? (
              <li className="ws-hero-desk__fact">
                <EditableContent
                  as="span"
                  fieldKey="hoursIcon"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-hero-desk__fact-icon-hit"
                >
                  <LucideIcon icon={hoursIcon} size="sm" className="ws-hero-desk__fact-icon" />
                </EditableContent>
                <EditableContent
                  fieldKey="hours"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-hero-desk__fact-text"
                >
                  {hours}
                </EditableContent>
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </section>
  );
}
